/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { useEffect } from 'react';
import { string, func, object, shape } from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'clsx';

import p from 'polyglot/polyglot';

import { getWebPImage } from 'utils/image';

import { TALENT_MAKES_OFFER, CLIENT_MAKES_OFFER } from 'actions/job';

import Select from 'components/Form/Input/Select/Select';
import DatePickerInput from 'components/Form/Input/DatePickerInput/DatePickerInput';
import TextInput from 'components/Form/Input/TextInput/TextInput';
import TrashIcon from 'gfx/vector/trash-icon.svg';

import styles from './talent-inputs.scss';

/**
 * This is the TalentInputs component class.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
const TalentInputs = ({
  bootstrap: { constants },
  talent_id,
  title,
  role_name,
  picture: { thumbnail_url },
  formData: {
    dates,
    pricing_method,
    compensation_amount,
    compensation_currency,
  },
  onRemove,
  onChange,
}) => {
  const pricingMethods = {
    [CLIENT_MAKES_OFFER]: p.t(
      'jobs.request_talent.form.pricing_method.options.CLIENT_MAKES_OFFER'
    ),
    [TALENT_MAKES_OFFER]: p.t(
      'jobs.request_talent.form.pricing_method.options.TALENT_MAKES_OFFER'
    ),
  };

  const onChangeInput = event => {
    const {
      target: { value, name },
    } = event;

    onChange(talent_id, name, value);
  };

  const onChangeDates = event => {
    const {
      target: { value },
    } = event;

    onChange(talent_id, 'dates', value);
  };

  const onClickRemove = event => {
    event.preventDefault();

    onRemove(talent_id);
  };

  useEffect(() => {
    // set default pricing method
    const defaultValue = Object.keys(pricingMethods)?.[0];
    if (defaultValue) onChange(talent_id, 'pricing_method', defaultValue);
  }, []);

  const hasFeeFields = pricing_method.value !== TALENT_MAKES_OFFER;

  return (
    <article className={styles.wrapper}>
      <div className={styles.imageContainer}>
        <div
          className={styles.image}
          style={{ backgroundImage: `url(${getWebPImage(thumbnail_url)})` }}
        />
        <p className={styles.imgCaptionRole}>{role_name}</p>
        <p className={styles.imgCaptionTitle}>{title}</p>
      </div>

      <div className={styles.form}>
        <header className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <h2 className={styles.role}>/ {role_name}</h2>
        </header>

        <div className={styles.inputs}>
          <div className={styles.date}>
            <label htmlFor={`${talent_id}-dates`} className={styles.labelText}>
              {p.t('jobs.send_requests.form.dates.label')}
            </label>
            <DatePickerInput
              id={`${talent_id}-dates`}
              name="dates"
              placeholder={p.t('jobs.send_requests.form.dates.placeholder')}
              value={dates.value}
              error={!dates.valid}
              onChange={onChangeDates}
              multiple
              disableTimeRange
            />
          </div>

          <div className={styles.input}>
            <label
              htmlFor={`${talent_id}-pricing_method`}
              className={styles.labelText}
            >
              {p.t('jobs.send_requests.form.pricing_method.label')}
            </label>
            <Select
              id={`${talent_id}-pricing_method`}
              name="pricing_method"
              required
              value={pricing_method.value}
              error={!pricing_method.valid}
              options={pricingMethods}
              onChange={onChangeInput}
            />
          </div>

          <div
            className={classNames(styles.input, {
              [styles.disabled]: !hasFeeFields,
              [styles.hiddenMobile]: !hasFeeFields,
            })}
          >
            <label
              htmlFor={`${talent_id}-compensation_amount`}
              className={styles.labelText}
            >
              {p.t('jobs.send_requests.form.compensation_amount.label')}
            </label>
            <TextInput
              id={`${talent_id}-compensation_amount`}
              name="compensation_amount"
              placeholder={p.t(
                'jobs.send_requests.form.compensation_amount.placeholder'
              )}
              value={compensation_amount.value}
              error={hasFeeFields && !compensation_amount.valid}
              disabled={!hasFeeFields}
              type="text"
              onChange={onChangeInput}
            />
          </div>

          <div
            className={classNames(styles.input, {
              [styles.disabled]: !hasFeeFields,
              [styles.hiddenMobile]: !hasFeeFields,
            })}
          >
            <label
              htmlFor={`${talent_id}-compensation_currency`}
              className={styles.labelText}
            >
              {p.t('jobs.send_requests.form.compensation_currency.label')}
            </label>
            <Select
              id={`${talent_id}-compensation_currency`}
              name="compensation_currency"
              placeholder={p.t(
                'jobs.send_requests.form.compensation_currency.placeholder'
              )}
              required
              value={compensation_currency.value}
              error={hasFeeFields && !compensation_currency.valid}
              options={constants.compensation_currency}
              disabled={!hasFeeFields}
              onChange={onChangeInput}
            />
          </div>
        </div>
        <div className={styles.options}>
          <button
            type="button"
            className={styles.removeButton}
            onClick={onClickRemove}
          >
            <TrashIcon className={styles.removeButtonIcon} />
            Remove
          </button>
        </div>
      </div>
    </article>
  );
};
/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
TalentInputs.propTypes = {
  bootstrap: shape({
    constants: object.isRequired,
  }).isRequired,
  talent_id: string.isRequired,
  title: string.isRequired,
  picture: shape({
    thumbnail_url: string,
  }).isRequired,
  onChange: func.isRequired,
  onRemove: func.isRequired,
  formData: shape({
    dates: object,
    pricing_method: object,
    compensation_amount: object,
    compensation_currency: object,
  }),
};

/**
 * Declare default props.
 *
 * @type {Object}
 */
TalentInputs.defaultProps = {
  formData: {
    dates: {
      value: '',
      valid: true,
    },
    pricing_method: {
      value: '',
      valid: true,
    },
    compensation_amount: {
      value: '',
      valid: true,
    },
    compensation_currency: {
      value: '',
      valid: true,
    },
  },
};

export default connect(({ bootstrap }) => ({ bootstrap }))(TalentInputs);
